import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ShieldCheck, 
  Cpu, 
  FileText, 
  Activity, 
  Zap, 
  Server, 
  ArrowRight, 
  CheckCircle2, 
  Network,
  Lock
} from 'lucide-react';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans selection:bg-blue-200">
      
      {/* --- NAVBAR --- */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-blue-600 text-white p-2 rounded-lg font-black text-xl leading-none">
              AI
            </div>
            <span className="text-2xl font-black text-slate-900 tracking-tight">FCAIO</span>
          </div>
          <div className="hidden md:flex items-center gap-8 font-semibold text-sm text-slate-600">
            <a href="#features" className="hover:text-blue-600 transition-colors">Platform</a>
            <a href="#sprint" className="hover:text-blue-600 transition-colors">The 5-Day Sprint</a>
            <a href="#pricing" className="hover:text-blue-600 transition-colors">Pricing</a>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-sm font-bold text-slate-700 hover:text-blue-600">Log In</Link>
            <Link to="/register" className="text-sm font-bold bg-slate-900 text-white px-5 py-2.5 rounded-lg hover:bg-slate-800 transition-shadow shadow-md">
              Start Free Trial
            </Link>
          </div>
        </div>
      </nav>

      {/* --- HERO SECTION --- */}
      <section className="relative pt-24 pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-100 via-slate-50 to-slate-50 -z-10" />
        
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-bold uppercase tracking-widest mb-8">
            <span className="flex h-2 w-2 rounded-full bg-blue-600 animate-pulse"></span>
            The Agentic AI Operating System
          </div>
          
          <h1 className="text-5xl md:text-7xl font-black text-slate-900 leading-[1.1] tracking-tight mb-8">
            Stop Guessing. <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              Start Governing.
            </span>
          </h1>
          
          <p className="text-xl text-slate-600 mb-10 max-w-3xl mx-auto leading-relaxed">
            FCAIO is the enterprise automated audit-readiness engine. We secure your autonomous agents, block PII leakage with our Zero-Trust Firewall, and synthesize AI ROI—all mapped strictly to the EU AI Act, GDPR, and ISO 42001.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register" className="w-full sm:w-auto flex items-center justify-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-700 hover:shadow-xl hover:shadow-blue-600/20 transition-all">
              Take the Readiness Grader <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/book-discovery" className="w-full sm:w-auto flex items-center justify-center gap-2 bg-white text-slate-800 border-2 border-slate-200 px-8 py-4 rounded-xl font-bold text-lg hover:border-slate-300 hover:bg-slate-50 transition-all">
              Book a Demo
            </Link>
          </div>
        </div>
      </section>

      {/* --- FEATURES GRID --- */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">Enterprise-Grade AI Controls</h2>
            <p className="text-slate-500 max-w-2xl mx-auto text-lg">Everything you need to move Generative AI from risky experimentation into secure, compliant production.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-slate-50 border border-slate-200 p-8 rounded-3xl hover:shadow-lg transition-shadow">
              <div className="bg-indigo-100 w-14 h-14 rounded-2xl flex items-center justify-center mb-6">
                <ShieldCheck className="w-7 h-7 text-indigo-700" />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-3">Zero-Trust AI Firewall</h3>
              <p className="text-slate-600 leading-relaxed">
                Bidirectional interception of all AI inference traffic. Automatically redacts PII, blocks Prompt Injections, and uses Semantic Caching to reduce your LLM API costs.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-slate-50 border border-slate-200 p-8 rounded-3xl hover:shadow-lg transition-shadow">
              <div className="bg-emerald-100 w-14 h-14 rounded-2xl flex items-center justify-center mb-6">
                <FileText className="w-7 h-7 text-emerald-700" />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-3">Dynamic SPDX AI-BOMs</h3>
              <p className="text-slate-600 leading-relaxed">
                Automated Software Bill of Materials generation. Instantly map your Shadow AI footprint and verify Data Processing Agreements (DPAs) for EU AI Act compliance.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-slate-50 border border-slate-200 p-8 rounded-3xl hover:shadow-lg transition-shadow">
              <div className="bg-amber-100 w-14 h-14 rounded-2xl flex items-center justify-center mb-6">
                <Zap className="w-7 h-7 text-amber-700" />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-3">ROI Synthesizer</h3>
              <p className="text-slate-600 leading-relaxed">
                Map your manual business workflows and let our Gemini-powered engine calculate FTE savings, automatically categorizing Quick Wins vs. Strategic Agentic Bets.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-slate-50 border border-slate-200 p-8 rounded-3xl hover:shadow-lg transition-shadow">
              <div className="bg-blue-100 w-14 h-14 rounded-2xl flex items-center justify-center mb-6">
                <Activity className="w-7 h-7 text-blue-700" />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-3">Continuous Control Monitoring</h3>
              <p className="text-slate-600 leading-relaxed">
                Stop relying on spreadsheets. FCAIO uses live firewall telemetry to auto-verify that your ISO 42001 and GDPR controls are actively functioning in production.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-slate-50 border border-slate-200 p-8 rounded-3xl hover:shadow-lg transition-shadow">
              <div className="bg-purple-100 w-14 h-14 rounded-2xl flex items-center justify-center mb-6">
                <Network className="w-7 h-7 text-purple-700" />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-3">Agentic Governance</h3>
              <p className="text-slate-600 leading-relaxed">
                Enforce the "Principle of Least Agency." Use our MCP (Model Context Protocol) interceptors to ensure autonomous AI agents cannot execute dangerous actions without human-in-the-loop approval.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-slate-50 border border-slate-200 p-8 rounded-3xl hover:shadow-lg transition-shadow">
              <div className="bg-rose-100 w-14 h-14 rounded-2xl flex items-center justify-center mb-6">
                <Server className="w-7 h-7 text-rose-700" />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-3">Executive Board Briefings</h3>
              <p className="text-slate-600 leading-relaxed">
                Translate complex AI risk into board-ready metrics. Generate real-time Readiness Heat Maps and calculate explicit financial exposure fines in one click.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* --- THE 5-DAY SPRINT METHODOLOGY --- */}
      <section id="sprint" className="py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black mb-4">The Tier 2 Diagnostic Sprint</h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg">We don't just sell software; we build your strategy. Deploy our 5-day accelerated engagement to map, secure, and synthesize your entire AI roadmap.</p>
          </div>

          <div className="grid md:grid-cols-5 gap-4">
            {[
              { day: 'Day 1', title: 'Executive Alignment', desc: 'Correlate AI initiatives directly with 2026 operational targets.' },
              { day: 'Day 2', title: 'Workflow Mapping', desc: 'Identify manual bottlenecks and assign Agentic/LLM intervention strategies.' },
              { day: 'Day 3', title: 'Infrastructure Grader', desc: 'Assess data silos, RBAC, and security posture for RAG readiness.' },
              { day: 'Day 4', title: 'Capability Mapping', desc: 'Run the ROI Synthesizer to plot Impact vs. Effort across the enterprise.' },
              { day: 'Day 5', title: 'Executive Readout', desc: 'Deliver the 6-Month Execution Roadmap and Corporate AI Policy.' }
            ].map((step, idx) => (
              <div key={idx} className="bg-slate-800 p-6 rounded-2xl border border-slate-700 relative">
                <div className="text-blue-400 font-black text-sm uppercase tracking-widest mb-2">{step.day}</div>
                <h4 className="text-lg font-bold mb-3">{step.title}</h4>
                <p className="text-slate-400 text-sm leading-relaxed">{step.desc}</p>
                {idx !== 4 && <ArrowRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-600 z-10" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- MEET THE CAIO SECTION --- */}
      <section className="py-24 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-bold mb-6 border border-blue-200">
                Executive Leadership
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-6">
                Meet Your Fractional CAIO
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Hi, I'm <strong className="text-slate-900">Kamalakannan Kamaraj</strong>. I bridge the gap between deep technical data science and executive boardroom strategy, ensuring your AI initiatives are technically sound, ethically governed, and commercially driven.
              </p>
              
              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-2 rounded-lg text-blue-600 mt-1">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">23+ Years Experience</h4>
                    <p className="text-slate-600 text-sm mt-1">Driving cross-functional enterprise digital transformation across Tech, Banking, and Healthcare.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600 mt-1">
                    <Zap className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">Proven Outcomes</h4>
                    <p className="text-slate-600 text-sm mt-1">Deployed GenAI architectures that delivered 30% faster delivery timelines and 25% performance gains.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-purple-100 p-2 rounded-lg text-purple-600 mt-1">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">Elite Credentials</h4>
                    <p className="text-slate-600 text-sm mt-1">MSc in AI & Machine Learning (UK), PMP Certified, and a Qualified Independent Director.</p>
                  </div>
                </div>
              </div>
              
              <Link 
                to="/book-discovery" 
                className="inline-block px-8 py-4 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl shadow-lg transition-all"
              >
                Discuss Your AI Strategy
              </Link>
            </div>
            
            <div className="relative">
              <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-2xl relative z-10">
                <img 
                  src="/kamal-profile.jpg" 
                  alt="Kamalakannan Kamaraj" 
                  className="w-32 h-32 rounded-full object-cover shadow-lg border-4 border-white mb-6 bg-slate-100"
                />
                
                <h3 className="text-2xl font-black text-slate-900 mb-1">Kamalakannan Kamaraj</h3>
                <p className="text-blue-600 font-bold mb-8">Fractional Chief AI Officer</p>
                
                <div className="grid grid-cols-2 gap-6 border-t border-slate-100 pt-6">
                  <div>
                    <div className="text-3xl font-black text-slate-900 mb-1">23+</div>
                    <div className="text-xs text-slate-500 font-bold uppercase tracking-widest">Years Exp.</div>
                  </div>
                  <div>
                    <div className="text-3xl font-black text-emerald-600 mb-1">30%</div>
                    <div className="text-xs text-slate-500 font-bold uppercase tracking-widest">Velocity Increase</div>
                  </div>
                  <div>
                    <div className="text-3xl font-black text-blue-600 mb-1">MSc</div>
                    <div className="text-xs text-slate-500 font-bold uppercase tracking-widest">AI / ML (UK)</div>
                  </div>
                  <div>
                    <div className="text-3xl font-black text-purple-600 mb-1">PMP</div>
                    <div className="text-xs text-slate-500 font-bold uppercase tracking-widest">Certified</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- PRICING & TIERS --- */}
      <section id="pricing" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">Choose Your Path to Compliance</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Free Trial */}
            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm flex flex-col">
              <h3 className="text-2xl font-black text-slate-900 mb-2">Free Trial</h3>
              <p className="text-slate-500 mb-6 flex-grow">Kick the tires. Assess your top 3 tools.</p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3 text-slate-700 font-medium"><CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> 7-Day Platform Access</li>
                <li className="flex items-start gap-3 text-slate-700 font-medium"><CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> 3 AI Tool Inventory Limit</li>
                <li className="flex items-start gap-3 text-slate-700 font-medium"><CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> Basic Risk Assessment</li>
              </ul>
              <Link to="/register?tier=0" className="block w-full text-center bg-slate-100 text-slate-900 font-bold py-3.5 rounded-xl hover:bg-slate-200 transition-colors mt-auto">
                Start Trial
              </Link>
            </div>

            {/* Tier 1 */}
            <div className="bg-blue-600 p-8 rounded-3xl border border-blue-700 shadow-xl transform md:-translate-y-4 relative flex flex-col">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-emerald-400 text-emerald-950 text-xs font-black uppercase tracking-widest px-4 py-1.5 rounded-full shadow-md">
                Most Popular
              </div>
              <h3 className="text-2xl font-black text-white mb-2">Tier 1: Platform</h3>
              <p className="text-blue-200 mb-6 flex-grow">Continuous governance and native firewalling.</p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3 text-white font-medium"><CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0" /> 20 AI Tool Inventory</li>
                <li className="flex items-start gap-3 text-white font-medium"><CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0" /> Native Zero-Trust Firewall</li>
                <li className="flex items-start gap-3 text-white font-medium"><CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0" /> SPDX 3.0 AI-BOM Generation</li>
                <li className="flex items-start gap-3 text-white font-medium"><CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0" /> Corporate Policy Generator</li>
              </ul>
              <Link to="/register?tier=1" className="block w-full text-center bg-white text-blue-600 font-black py-3.5 rounded-xl hover:bg-slate-50 transition-colors shadow-lg mt-auto">
                Subscribe to Tier 1
              </Link>
            </div>

            {/* Tier 2 */}
            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm flex flex-col">
              <h3 className="text-2xl font-black text-slate-900 mb-2">Tier 2: Sprint</h3>
              <p className="text-slate-500 mb-6 flex-grow">Accelerated 5-Day diagnostic deployment.</p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3 text-slate-700 font-medium"><CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> Everything in Tier 1</li>
                <li className="flex items-start gap-3 text-slate-700 font-medium"><CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> Dedicated FCAIO Consultant</li>
                <li className="flex items-start gap-3 text-slate-700 font-medium"><CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> Workflow ROI Synthesizer</li>
                <li className="flex items-start gap-3 text-slate-700 font-medium"><CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> Executive Board Readout</li>
              </ul>
              <Link to="/register?tier=2" className="block w-full text-center bg-slate-900 text-white font-bold py-3.5 rounded-xl hover:bg-slate-800 transition-colors shadow-md mt-auto">
                Request a Sprint
              </Link>
            </div>
          </div>
          
          <div className="mt-12 text-center">
             <p className="text-slate-500 font-medium flex items-center justify-center gap-2">
               <Lock className="w-4 h-4" /> Need BYO-Proxy and Air-Gapped Data Residency? <Link to="/book-discovery" className="text-blue-600 font-bold hover:underline">Ask about Tier 3: Enterprise Retainer.</Link>
             </p>
          </div>
        </div>
      </section>

      {/* --- FOOTER --- */}
      <footer className="bg-slate-950 py-12 text-center text-slate-400 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-6 flex flex-col items-center">
          <div className="flex items-center gap-2 mb-6 grayscale opacity-50">
            <div className="bg-white text-slate-900 p-1.5 rounded font-black text-sm leading-none">AI</div>
            <span className="text-lg font-black text-white tracking-tight">FCAIO</span>
          </div>
          <p className="text-sm">© {new Date().getFullYear()} FCAIO Platform. All rights reserved.</p>
          <p className="text-xs mt-2 text-slate-600 font-bold">BUILT FOR THE EU AI ACT, GDPR, AND ISO 42001 COMPLIANCE.</p>
        </div>
      </footer>
    </div>
  );
}